import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import type { MediaItem } from '../stores/types/media';
import { selectFeatureFlag } from '../stores/selectors/blp-feature-flags';
import { FeatureFlagsEnum } from '../types/blp-feature-flags';
import { isEpidemicSoundMedia } from '../utils/media';

/**
 * platform.epidemic_sound gates every Epidemic Sound feature in the client.
 *
 * Long term the APIs stop returning Epidemic-sourced media while the flag is
 * off, so nothing here would ever match. Until then the UI has to turn the
 * features off itself: affordances that only exist to edit Epidemic media are
 * hidden rather than left inert, and the Epidemic-only surfaces (track version
 * picker, version re-fetch on duration change) never render or fire.
 *
 * Media from any other provider - uploads, capsule - is unaffected.
 */
export const useEpidemicSound = () => {
	const epidemicSoundEnabled = !!useSelector(selectFeatureFlag(FeatureFlagsEnum.epidemic_sound));

	/** true when this item is Epidemic-sourced and the feature is turned off */
	const isEpidemicSoundBlocked = useCallback((
		mediaItem?: Pick<MediaItem, 'provider'> | null,
	): boolean => isEpidemicSoundMedia(mediaItem) && !epidemicSoundEnabled, [epidemicSoundEnabled]);

	return { epidemicSoundEnabled, isEpidemicSoundBlocked };
};

export default useEpidemicSound;
